import React, { useState } from 'react';
import { Package, X, ShoppingCart, Clock, Calendar, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

interface Product {
  id: string;
  name: string;
  image: string;
  description: string;
  detailDescription: string;
  installDate?: string;
  warrantyPeriod?: string;
  status: 'active' | 'inactive';
  price?: number;
  lastOrderDate?: string;
}

const MyProducts: React.FC = () => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Mock product data
  const products: Product[] = [
    {
      id: "product-1",
      name: "Smart Floor Underlayment",
      image: "/product%20images/product_underlayment.png",
      description: "Advanced pressure-sensitive underlayment with built-in sensors",
      detailDescription: "Our patented smart floor underlayment is installed beneath your existing flooring to monitor movement patterns, detect falls, and provide 24/7 health insights without cameras or wearables. The ultra-thin design works with most flooring types and is completely invisible once installed.",
      installDate: "2023-06-15",
      warrantyPeriod: "5 years",
      status: "active",
      price: 499,
      lastOrderDate: "2023-05-30"
    },
    {
      id: "product-2",
      name: "Floor Sensor Hub",
      image: "/product%20images/product_box.png",
      description: "Central processing unit for your smart floor system",
      detailDescription: "The Floor Sensor Hub is the brain of your smart floor system, collecting and analyzing data from the underlayment sensors to provide actionable insights. Features include wireless connectivity, automatic updates, and advanced machine learning algorithms that improve over time.",
      installDate: "2023-06-15",
      warrantyPeriod: "3 years",
      status: "active",
      price: 99,
      lastOrderDate: "2023-05-30"
    },
    {
      id: "product-3",
      name: "Power Adapter",
      image: "/product%20images/product_power.png",
      description: "Specialized power supply for the Floor Sensor Hub",
      detailDescription: "This UL-certified power adapter is specifically designed for the Floor Sensor Hub, providing stable, continuous power with built-in surge protection. The adapter includes battery backup to ensure your system continues monitoring even during power outages.",
      installDate: "2023-06-15",
      warrantyPeriod: "2 years",
      status: "active",
      price: 39,
      lastOrderDate: "2023-05-30"
    },
    {
      id: "product-4",
      name: "Connection Cable",
      image: "/product%20images/product_cable.png",
      description: "High-quality cable connecting sensors to the hub",
      detailDescription: "Our premium connection cable ensures reliable data transmission between the underlayment sensors and the Floor Sensor Hub. The 10-foot braided cable is designed for durability and includes gold-plated connectors to prevent signal degradation over time.",
      installDate: "2023-06-15",
      warrantyPeriod: "2 years",
      status: "active",
      price: 9,
      lastOrderDate: "2023-05-30"
    }
  ];

  // Open the modal with the selected product
  const openProductModal = (product: Product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  // Close the modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  // Format price as currency
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  // Format date to readable format
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">My Products</h2>
        <p className="text-gray-600">
          View and manage the products installed in your smart flooring system
        </p>
      </div>

      {/* Product Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        {products.map(product => (
          <div
            key={product.id}
            className="bg-white rounded-lg shadow hover:shadow-md transition-shadow overflow-hidden"
          >
            <div className="p-6">
              <div className="flex flex-col md:flex-row items-center">
                <div className="flex-shrink-0 w-32 h-32 bg-gray-50 rounded-lg flex items-center justify-center mb-4 md:mb-0 md:mr-6">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="max-h-28 max-w-28 object-contain"
                  />
                </div>
                <div className="flex-grow">
                  <div className="flex items-start justify-between">
                    <h3 className="text-lg font-semibold">{product.name}</h3>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      product.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {product.status === 'active' ? (
                        <><CheckCircle size={12} className="mr-1" /> Active</>
                      ) : (
                        <><AlertCircle size={12} className="mr-1" /> Inactive</>
                      )}
                    </span>
                  </div>
                  <p className="text-gray-600 mt-1">{product.description}</p>
                  
                  <div className="mt-3 flex flex-wrap items-center text-sm text-gray-500">
                    <div className="flex items-center mr-4 mb-2">
                      <Calendar size={14} className="mr-1" />
                      <span>Installed: {product.installDate ? formatDate(product.installDate) : 'N/A'}</span>
                    </div>
                    <div className="flex items-center mb-2">
                      <Clock size={14} className="mr-1" />
                      <span>Warranty: {product.warrantyPeriod}</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => openProductModal(product)}
                    className="mt-3 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm flex items-center"
                  >
                    <Package size={16} className="mr-2" />
                    View Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Product Modal */}
      {isModalOpen && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start p-6 border-b">
              <h3 className="text-xl font-semibold">{selectedProduct.name}</h3>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6">
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/3 flex-shrink-0 mb-6 md:mb-0 md:mr-6">
                  <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center h-48">
                    <img
                      src={selectedProduct.image}
                      alt={selectedProduct.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  
                  <div className="mt-4 bg-blue-50 rounded-lg p-4">
                    <div className="font-semibold text-blue-800 mb-2">Product Details</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <span className={`font-medium ${
                          selectedProduct.status === 'active' ? 'text-green-600' : 'text-yellow-600'
                        }`}>
                          {selectedProduct.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Price:</span>
                        <span className="font-medium">{selectedProduct.price ? formatPrice(selectedProduct.price) : 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Installed:</span>
                        <span className="font-medium">{selectedProduct.installDate ? formatDate(selectedProduct.installDate) : 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Warranty:</span>
                        <span className="font-medium">{selectedProduct.warrantyPeriod}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Last Ordered:</span>
                        <span className="font-medium">{selectedProduct.lastOrderDate ? formatDate(selectedProduct.lastOrderDate) : 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="md:w-2/3">
                  <div className="prose max-w-none">
                    <p className="text-gray-800 mb-4">
                      {selectedProduct.detailDescription}
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 mt-4">
                    <h4 className="font-semibold mb-2">Maintenance Tips</h4>
                    <ul className="text-sm text-gray-700 space-y-2">
                      <li className="flex items-start">
                        <CheckCircle size={16} className="text-green-500 mr-2 mt-0.5" />
                        <span>Regularly check connections to ensure secure fit</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle size={16} className="text-green-500 mr-2 mt-0.5" />
                        <span>Keep the sensor hub in a well-ventilated area</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle size={16} className="text-green-500 mr-2 mt-0.5" />
                        <span>Avoid exposing components to excessive moisture</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle size={16} className="text-green-500 mr-2 mt-0.5" />
                        <span>Contact support for any performance concerns</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="mt-6 flex flex-col sm:flex-row sm:justify-between space-y-3 sm:space-y-0">
                    <button
                      onClick={() => alert(`Re-ordering ${selectedProduct.name}`)}
                      className="bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center sm:justify-start"
                    >
                      <ShoppingCart size={18} className="mr-2" />
                      Re-order Product
                    </button>
                    
                    <button
                      onClick={() => alert(`Scheduling service for ${selectedProduct.name}`)}
                      className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center sm:justify-start"
                    >
                      <RefreshCw size={18} className="mr-2" />
                      Schedule Service
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProducts; 